import { Icon } from "@bms-ai/bms-ai-ui-lib/components";
import { useState } from "react";
import FailIcon from "../../../_assets/icons/FailIcon";
import WarningIcon from "../../../_assets/icons/WarningIcon";
// import LikeIcon from "../../../_assets/icons/LikeIcon";
// import DislikeIcon from "../../../_assets/icons/DislikeIcon";
import { formatCheckId } from "../../../helpers/helpers";
import { formatMessage } from "../QAResultsNew";

// Common function to render finding message - handles special cases like copy_vs_manuscript
const renderFindingMessage = (finding: Finding): React.ReactNode => {
  if (
    (finding.check_id?.toLowerCase() === "copy_vs_manuscript" ||
      finding.check_id?.toLowerCase() === "isi_copy_vs_manuscript") &&
    finding.message
  ) {
    try {
      // Parse JSON from message string
      const data = JSON.parse(finding.message);

      // Sort entries by status: Mismatch first, then Observation, then Match
      const statusOrder: Record<string, number> = {
        MISMATCH: 1,
        OBSERVATION: 2,
        MATCH: 3,
      };

      const sortedEntries = Object.entries(data).sort(
        ([, detailsA]: [string, any], [, detailsB]: [string, any]) => {
          const statusA = detailsA.status || "";
          const statusB = detailsB.status || "";
          const orderA = statusOrder[statusA] || 999;
          const orderB = statusOrder[statusB] || 999;
          return orderA - orderB;
        },
      );

      return (
        <div className="copy-vs-manuscript-table">
          <table>
            <thead>
              <tr>
                <th>Section Name</th>
                <th>Status</th>
                <th>Explanation</th>
                <th>Expected Text</th>
                <th>Actual Text</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map(([sectionName, details]: [string, any]) => (
                <tr key={sectionName}>
                  <td>{sectionName}</td>
                  <td>{details.status || ""}</td>
                  <td>{details.explanation || ""}</td>
                  <td>{details.expected_text || ""}</td>
                  <td>{details.actual_text || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } catch (error) {
      console.error("Failed to parse copy_vs_manuscript JSON:", error);
      return formatMessage(finding.message);
    }
  } else if (
    finding.check_id?.toLowerCase() === "animation_notes_match" &&
    finding.message
  ) {
    try {
      const data = JSON.parse(finding.message);
      const details = Array.isArray(data) ? data : data.details || [];
      return (
        <div className="copy-vs-manuscript-table">
          <table>
            <thead>
              <tr>
                <th>Frame Number</th>
                <th>Status</th>
                <th>Explanation</th>
                <th>Expected</th>
                <th>Actual</th>
              </tr>
            </thead>
            <tbody>
              {details.map((detail: any, index: number) => (
                <tr key={index}>
                  <td>{detail.frame_number || ""}</td>
                  <td>{detail.status || ""}</td>
                  <td>{detail.explanation || ""}</td>
                  <td>{detail.expected || ""}</td>
                  <td>{detail.actual || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    } catch (error) {
      console.error("Failed to parse animation_notes_match JSON:", error);
      return formatMessage(finding.message);
    }
  }
  return formatMessage(finding.message);
};

interface Finding {
  finding_id: string;
  check_id: string;
  category: string;
  result: "PASS" | "FAIL" | "BRAND_GUIDELINE_NOT_AVAILABLE" | "NA";
  severity: "INFO" | "MINOR" | "MAJOR" | "CRITICAL";
  message: string;
  frame_id: string | null;
  doc_id: string | null;
  source_type: string | null;
  actual: string | null;
  expected: string | null;
  recommendation: string | null;
  used_llm: boolean;
  confidence: number | null;
  location?: string | null;
}

interface StoryboardSectionProps {
  channel: string;
  frameFindings: Finding[];
  storyboardFindings: Finding[];
  otherStoryboardFindings: Finding[];
  frameImages: Map<string, string>;
  frameSasUrls: Map<string, string>;
  expandedFrames: Set<string>;
  expandedFindings: Set<string>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
  isResultsComplete: boolean;
}

export const StoryboardSection: React.FC<StoryboardSectionProps> = ({
  channel,
  frameFindings,
  storyboardFindings,
  otherStoryboardFindings,
  frameImages,
  frameSasUrls,
  expandedFrames,
  expandedFindings,
  setExpandedFrames,
  setExpandedFindings,
  isResultsComplete,
}) => {
  const isBanner = channel === "banner" || channel === "animated_banner";
  const hasStoryboardFindings = isBanner
    ? frameFindings.length > 0
    : storyboardFindings.length > 0;
  const hasOtherFindings = otherStoryboardFindings.length > 0;

  if (!hasStoryboardFindings && !hasOtherFindings) return null;

  // Determine which findings to show
  const selectedKey =
    expandedFrames.size > 0
      ? Array.from(expandedFrames)[0]
      : expandedFindings.size > 0
        ? Array.from(expandedFindings)[0]
        : null;

  let selectedFindings: Finding[] | null = null;
  let selectedFindingsComponent: React.ReactNode = null;

  if (selectedKey) {
    if (isBanner) {
      // Banner: selectedKey is frame_id
      const frameId = selectedKey;
      const frameData = frameFindings.filter((f) => f.frame_id === frameId);
      if (frameData.length > 0) {
        selectedFindings = frameData;
      }
    } else {
      // Non-banner: selectedKey might be check_id or frame_id
      if (selectedKey.startsWith("check_")) {
        const checkId = selectedKey.replace("check_", "");
        const checkFindings = storyboardFindings.filter(
          (f) => f.check_id === checkId,
        );
        if (checkFindings.length > 0) {
          if (checkId === "blur_pixelation_check") {
            selectedFindingsComponent = (
              <BlurPixelationFindings
                findings={checkFindings}
                frameSasUrls={frameSasUrls}
                isResultsComplete={isResultsComplete}
              />
            );
          } else if (checkId === "image_cropping_check") {
            selectedFindingsComponent = (
              <BlurPixelationFindings
                findings={checkFindings}
                frameSasUrls={frameSasUrls}
                isResultsComplete={isResultsComplete}
              />
            );
          } else if (
            checkId.toLowerCase() === "hoe_alignment_check" ||
            checkId.toLowerCase() === "url_check"
          ) {
            selectedFindingsComponent = (
              <CheckFindings findings={checkFindings} useLocation={true} />
            );
          } else {
            // if (channel === "animated_banner") {
            //   selectedFindingsComponent = (
            //     <CheckFindings
            //       findings={checkFindings}
            //       showFrameId={true}
            //       useLocation={true}
            //       isResultsComplete={isResultsComplete}
            //     />
            //   );
            // } else {
            selectedFindingsComponent = (
              <CheckFindings
                findings={checkFindings}
                useLocation={true}
                isResultsComplete={isResultsComplete}
              />
            );
            // }
          }
        }
      }
    }

    // Check otherStoryboardFindings
    if (!selectedFindingsComponent && selectedKey.startsWith("check_")) {
      const checkId = selectedKey.replace("check_", "");
      const checkFindings = otherStoryboardFindings.filter(
        (f) => f.check_id === checkId,
      );
      if (checkFindings.length > 0) {
        if (
          channel === "animated_banner" &&
          checkId.toLowerCase() === "animation_notes_match"
        ) {
          selectedFindingsComponent = (
            <CheckFindings
              findings={checkFindings}
              isResultsComplete={isResultsComplete}
            />
          );
        } else {
          selectedFindingsComponent = (
            <CheckFindings
              findings={checkFindings}
              useLocation={true}
              isResultsComplete={isResultsComplete}
            />
          );
        }
      }
    }
  }

  // If we have selectedFindings but no specific component, use default
  if (selectedFindings && !selectedFindingsComponent) {
    selectedFindingsComponent = (
      <FindingsByStatus
        findings={selectedFindings}
        expandedFindings={expandedFindings}
        setExpandedFindings={setExpandedFindings}
        isResultsComplete={isResultsComplete}
      />
    );
  }

  return (
    <div className="card-based-section">
      <div className="cards-container-left">
        {isBanner && frameFindings.length > 0 && (
          <BannerFrames
            frameFindings={frameFindings}
            frameImages={frameImages}
            expandedFrames={expandedFrames}
            setExpandedFrames={setExpandedFrames}
            setExpandedFindings={setExpandedFindings}
          />
        )}

        {!isBanner && storyboardFindings.length > 0 && (
          <NonBannerStoryboard
            storyboardFindings={storyboardFindings}
            expandedFrames={expandedFrames}
            setExpandedFrames={setExpandedFrames}
            setExpandedFindings={setExpandedFindings}
          />
        )}

        {hasOtherFindings && (
          <OtherFindings
            findings={otherStoryboardFindings}
            expandedFindings={expandedFindings}
            setExpandedFindings={setExpandedFindings}
            setExpandedFrames={setExpandedFrames}
          />
        )}

        {!isResultsComplete && (
          <div style={{ textAlign: "center", color: "#999" }}>
            <div className="skeleton-finding-card">
              <div
                className="skeleton-label"
                style={{ marginBottom: "8px" }}
              ></div>
              <div
                className="skeleton-text"
                style={{ width: "80%", marginBottom: "8px" }}
              ></div>
              <div className="skeleton-text" style={{ width: "60%" }}></div>
            </div>
          </div>
        )}
      </div>

      {selectedFindingsComponent && (
        <div className="findings-container-right">
          {selectedFindingsComponent}
        </div>
      )}
    </div>
  );
};

// Banner channel frames component
const BannerFrames: React.FC<{
  frameFindings: Finding[];
  frameImages: Map<string, string>;
  expandedFrames: Set<string>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
  frameFindings,
  frameImages,
  expandedFrames,
  setExpandedFrames,
  setExpandedFindings,
}) => {
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);

  const handleImageClick = (
    e: React.MouseEvent,
    frameId: string,
    index: number,
  ) => {
    e.stopPropagation();
    setSelectedImageId(frameId);
    setSelectedImageIndex(index);
  };

  const closeModal = () => {
    setSelectedImageId(null);
  };

  // Group frameFindings by frame_id
  const framesMap = new Map<
    string,
    { frame_id: string; doc_id: string; findings: Finding[] }
  >();

  frameFindings.forEach((finding) => {
    if (finding.frame_id && finding.doc_id) {
      if (!framesMap.has(finding.frame_id)) {
        framesMap.set(finding.frame_id, {
          frame_id: finding.frame_id,
          doc_id: finding.doc_id,
          findings: [],
        });
      }
      framesMap.get(finding.frame_id)!.findings.push(finding);
    }
  });

  const bannerFrames = Array.from(framesMap.values()).sort((a, b) => {
    const aMatch = a.frame_id.match(/_frame_(\d+)$/);
    const bMatch = b.frame_id.match(/_frame_(\d+)$/);
    const aNum = aMatch ? parseInt(aMatch[1], 10) : 0;
    const bNum = bMatch ? parseInt(bMatch[1], 10) : 0;
    return aNum - bNum;
  });

  // Only select if the selected ID is in this section
  const selectedFrameId =
    expandedFrames.size > 0 ? Array.from(expandedFrames)[0] : null;

  const handleCardSelect = (frameId: string) => {
    setExpandedFindings(new Set()); // Clear other section selections
    setExpandedFrames(new Set([frameId]));
  };

  return (
    <>
      {bannerFrames.map((frameData, index) => {
        const frameMatch = frameData.frame_id.match(/_frame_(\d+)$/);
        const frameNumber = frameMatch
          ? parseInt(frameMatch[1], 10)
          : index + 1;
        const displayLabel = `Frame ${frameNumber}`;
        const isSelected = frameData.frame_id === selectedFrameId;
        const imageUrl = frameImages.get(frameData.frame_id);

        const failedCount = frameData.findings.filter(
          (f) => f.result === "FAIL",
        ).length;
        const warningCount = frameData.findings.filter(
          (f) => f.result !== "PASS" && f.result !== "FAIL",
        ).length;

        return (
          <div
            key={frameData.frame_id}
            className={`frame-card ${isSelected ? "selected" : ""}`}
            onClick={() => handleCardSelect(frameData.frame_id)}
          >
            <div className="frame-card-content-wrapper">
              <div className="frame-card-image-section">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={displayLabel}
                    className="frame-card-image"
                    onClick={(e) => handleImageClick(e, frameData.frame_id, index)}
                    style={{ cursor: "pointer" }}
                  />
                ) : (
                  <div className="frame-card-placeholder">
                    <Icon type="document" size="medium" />
                  </div>
                )}
              </div>
              <div className="frame-card-text-section">
                <div className="frame-card-label">
                  Image {index + 1} of {bannerFrames.length}
                </div>
                <div className="frame-card-counts">
                  {warningCount > 0 && (
                    <span className="count-badge warning">{warningCount}</span>
                  )}
                  {failedCount > 0 && (
                    <span className="count-badge failed">{failedCount}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Image Preview Modal */}
      {selectedImageId && (
        <div className="image-preview-modal-overlay" onClick={closeModal}>
          <div
            className="image-preview-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-preview-header">
              <div className="image-preview-title">
                Image {selectedImageIndex + 1} of {bannerFrames.length}
              </div>
              <button className="image-preview-close" onClick={closeModal}>
                <Icon type="close" size="small" />
              </button>
            </div>
            {frameImages.get(selectedImageId) && (
              <img
                src={frameImages.get(selectedImageId)!}
                alt="Full preview"
                className="image-preview-full"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

// Non-banner storyboard component
const NonBannerStoryboard: React.FC<{
  storyboardFindings: Finding[];
  expandedFrames: Set<string>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
  storyboardFindings,
  expandedFrames,
  setExpandedFrames,
  setExpandedFindings,
}) => {
  // Group by check_id
  const findingsByCheckId = storyboardFindings.reduce(
    (acc, finding) => {
      const checkId = finding.check_id || "unknown";
      if (!acc[checkId]) {
        acc[checkId] = [];
      }
      acc[checkId].push(finding);
      return acc;
    },
    {} as Record<string, Finding[]>,
  );

  return (
    <>
      {Object.entries(findingsByCheckId).map(([checkId, checkFindings]) => (
        <CheckCard
          key={checkId}
          checkId={checkId}
          checkFindings={checkFindings}
          expandedFrames={expandedFrames}
          setExpandedFrames={setExpandedFrames}
          setExpandedFindings={setExpandedFindings}
          // checkNumber={index + 1}
        />
      ))}
    </>
  );
};

// Check card component (consolidating BlurPixelationCheck and FullWidthCheck)
const CheckCard: React.FC<{
  checkId: string;
  checkFindings: Finding[];
  expandedFrames: Set<string>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
  // checkNumber: number;
}> = ({
  checkId,
  checkFindings,
  expandedFrames,
  setExpandedFrames,
  setExpandedFindings,
  // checkNumber,
}) => {
  const checkKey = `check_${checkId}`;

  const isSelected = expandedFrames.has(checkKey);

  const failedCount = checkFindings.filter((f) => f.result === "FAIL").length;
  const warningCount = checkFindings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  ).length;

  const description = null;

  const handleCardSelect = () => {
    setExpandedFindings(new Set());
    setExpandedFrames(new Set([checkKey]));
  };

  return (
    <div
      className={`frame-card check-card ${isSelected ? "selected" : ""}`}
      onClick={handleCardSelect}
    >
      <div className="check-card-body">
        <div className="check-card-title">{formatCheckId(checkId)}</div>
        {description && (
          <div className="check-card-description">{description}</div>
        )}
      </div>
      <div className="check-card-header">
        {/* <div className="check-card-label">Check {checkNumber}</div> */}
        <div className="frame-card-counts">
          {warningCount > 0 && (
            <span className="count-badge warning">{warningCount}</span>
          )}
          {failedCount > 0 && (
            <span className="count-badge failed">{failedCount}</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Blur pixelation findings component - shows findings grouped by status with frame URLs
const BlurPixelationFindings: React.FC<{
  findings: Finding[];
  frameSasUrls: Map<string, string>;
  isResultsComplete: boolean;
}> = ({ findings, frameSasUrls, isResultsComplete }) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["failed", "warnings"]),
  );
  // const [feedbackState, setFeedbackState] = useState<
  //   Record<string, { liked: boolean; disliked: boolean; feedback?: string }>
  // >({});
  // const [feedbackModal, setFeedbackModal] = useState<{
  //   isOpen: boolean;
  //   findingId: string | null;
  // }>({ isOpen: false, findingId: null });
  // const [feedbackText, setFeedbackText] = useState("");

  // const handleBlurImageClick = async (
  //   e: React.MouseEvent<HTMLAnchorElement>,
  //   url: string,
  // ) => {
  //   e.preventDefault();
  //   try {
  //     const response = await fetch(url);
  //     const blob = await response.blob();

  //     // Force blob to be an image type to prevent download
  //     const imageType = blob.type.startsWith("image/")
  //       ? blob.type
  //       : "image/png";
  //     const imageBlob = new Blob([blob], { type: imageType });
  //     const objectUrl = URL.createObjectURL(imageBlob);

  //     // Open new window and render image directly
  //     const newWindow = window.open("", "_blank");
  //     if (newWindow) {
  //       newWindow.document.write(`
  //         <html>
  //           <head><title>Image Preview</title></head>
  //           <body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#1a1a1a;">
  //             <img src="${objectUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;" />
  //           </body>
  //         </html>
  //       `);
  //       newWindow.document.close();
  //     }
  //   } catch (error) {
  //     console.error("Failed to open image:", error);
  //     window.open(url, "_blank");
  //   }
  // };
  const handleBlurImageClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    e.preventDefault();

    try {
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Failed to open image:", error);
    }
  };

  // const handleLike = (findingId: string) => {
  //   setFeedbackState((prev) => ({
  //     ...prev,
  //     [findingId]: { liked: true, disliked: false, feedback: undefined },
  //   }));
  // };

  // const handleDislike = (findingId: string) => {
  //   setFeedbackModal({ isOpen: true, findingId });
  //   setFeedbackText("");
  // };

  // const handleFeedbackSubmit = () => {
  //   const findingId = feedbackModal.findingId;
  //   if (findingId && feedbackText.trim()) {
  //     setFeedbackState((prev) => ({
  //       ...prev,
  //       [findingId]: { liked: false, disliked: true, feedback: feedbackText },
  //     }));
  //     setFeedbackModal({ isOpen: false, findingId: null });
  //     setFeedbackText("");
  //   }
  // };

  // const handleFeedbackCancel = () => {
  //   setFeedbackModal({ isOpen: false, findingId: null });
  //   setFeedbackText("");
  // };

  const failedFindings = findings.filter((f) => f.result === "FAIL");
  const warningFindings = findings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  );
  const passedFindings = findings.filter((f) => f.result === "PASS");

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  const renderFindingsList = (findingsList: Finding[]) => {
    return findingsList.map((finding) => {
      const frameSasUrl = finding.frame_id
        ? frameSasUrls.get(finding.frame_id)
        : null;
      // const feedback = feedbackState[finding.finding_id];

      return (
        <div key={finding.finding_id} className="status-finding-item">
          <div className="finding-header">
            <span className="finding-title">
              Frame:
              {finding.frame_id && (
                <>
                  {frameSasUrl ? (
                    <a
                      className="frame-sas-link"
                      href={frameSasUrl}
                      onClick={(e) => handleBlurImageClick(e, frameSasUrl)}
                    >
                      {finding.frame_id}
                    </a>
                  ) : (
                    <span>{finding.frame_id}</span>
                  )}
                </>
              )}
            </span>
            {/* <div className="finding-feedback">
              <button
                className={`feedback-btn like-btn ${feedback?.liked ? "active" : ""}`}
                title="Like"
                onClick={() => handleLike(finding.finding_id)}
              >
                <LikeIcon fill={feedback?.liked ? "#BE2BBB" : "#9fa8b8"} />
              </button>
              <button
                className={`feedback-btn dislike-btn ${feedback?.disliked ? "active" : ""}`}
                title="Dislike"
                onClick={() => handleDislike(finding.finding_id)}
              >
                <DislikeIcon fill={feedback?.disliked ? "#BE2BBB" : "#9fa8b8"} />
              </button>
            </div> */}
          </div>
          <div className="finding-content">
            {finding.message && (
              <div className="finding-message">
                {formatMessage(finding.message)}
              </div>
            )}
            {(finding.actual || finding.expected || finding.recommendation) && (
              <div className="finding-recommendation">
                {finding.recommendation || finding.expected || finding.actual}
              </div>
            )}
          </div>
          {/* <div className="finding-feedback">
            <button className="feedback-btn like-btn" title="Like">
              <LikeIcon />
            </button>
            <button className="feedback-btn dislike-btn" title="Dislike">
              <DislikeIcon />
            </button>
          </div> */}
        </div>
      );
    });
  };

  return (
    <>
      <div className="findings-by-status">
        {failedFindings.length > 0 && (
          <div className="status-section failed">
            <div
              className="status-header"
              onClick={() => toggleSection("failed")}
            >
              <Icon
                type={
                  expandedSections.has("failed") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <FailIcon className="status-icon" />
              <span className="status-title">
                Failed ({failedFindings.length}{" "}
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
                )
              </span>
            </div>
            {expandedSections.has("failed") && (
              <div className="status-content">
                {renderFindingsList(failedFindings)}
              </div>
            )}
          </div>
        )}

        {warningFindings.length > 0 && (
          <div className="status-section warnings">
            <div
              className="status-header"
              onClick={() => toggleSection("warnings")}
            >
              <Icon
                type={
                  expandedSections.has("warnings")
                    ? "chevron-up"
                    : "chevron-down"
                }
                size="small"
              />
              <WarningIcon className="status-icon" />
              <span className="status-title">
                Observations ({warningFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("warnings") && (
              <div className="status-content">
                {renderFindingsList(warningFindings)}
              </div>
            )}
          </div>
        )}

        {passedFindings.length > 0 && (
          <div className="status-section passed">
            <div
              className="status-header"
              onClick={() => toggleSection("passed")}
            >
              <Icon
                type={
                  expandedSections.has("passed") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <Icon type="check-round" size="small" className="status-icon" />
              <span className="status-title">
                Passed ({passedFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("passed") && (
              <div className="status-content">
                {renderFindingsList(passedFindings)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      {/* {feedbackModal.isOpen && (
        <div className="feedback-modal-overlay" onClick={handleFeedbackCancel}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>Provide Feedback</h3>
              <button
                className="feedback-modal-close"
                onClick={handleFeedbackCancel}
              >
                <Icon type="close" size="small" />
              </button>
            </div>
            <div className="feedback-modal-body">
              <p>Please let us know why you disagree with this finding:</p>
              <textarea
                className="feedback-textarea"
                placeholder="Enter your feedback here..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={4}
              />
            </div>
            <div className="feedback-modal-footer">
              <button
                className="feedback-modal-btn cancel"
                onClick={handleFeedbackCancel}
              >
                Cancel
              </button>
              <button
                className="feedback-modal-btn submit"
                onClick={handleFeedbackSubmit}
                disabled={!feedbackText.trim()}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )} */}
    </>
  );
};

// Check findings component - shows findings grouped by status with frame_id
const CheckFindings: React.FC<{
  findings: Finding[];
  showFrameId?: boolean;
  useLocation?: boolean;
  isResultsComplete?: boolean;
}> = ({
  findings,
  showFrameId = false,
  useLocation = false,
  isResultsComplete = true,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["failed", "warnings"]),
  );
  // const [feedbackState, setFeedbackState] = useState<
  //   Record<string, { liked: boolean; disliked: boolean; feedback?: string }>
  // >({});
  // const [feedbackModal, setFeedbackModal] = useState<{
  //   isOpen: boolean;
  //   findingId: string | null;
  // }>({ isOpen: false, findingId: null });
  // const [feedbackText, setFeedbackText] = useState("");

  const failedFindings = findings.filter((f) => f.result === "FAIL");
  const warningFindings = findings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  );
  const passedFindings = findings.filter((f) => f.result === "PASS");

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // const handleLike = (findingId: string) => {
  //   setFeedbackState((prev) => ({
  //     ...prev,
  //     [findingId]: { liked: true, disliked: false, feedback: undefined },
  //   }));
  // };

  // const handleDislike = (findingId: string) => {
  //   setFeedbackModal({ isOpen: true, findingId });
  //   setFeedbackText("");
  // };

  // const handleFeedbackSubmit = () => {
  //   const findingId = feedbackModal.findingId;
  //   if (findingId && feedbackText.trim()) {
  //     setFeedbackState((prev) => ({
  //       ...prev,
  //       [findingId]: { liked: false, disliked: true, feedback: feedbackText },
  //     }));
  //     setFeedbackModal({ isOpen: false, findingId: null });
  //     setFeedbackText("");
  //   }
  // };

  // const handleFeedbackCancel = () => {
  //   setFeedbackModal({ isOpen: false, findingId: null });
  //   setFeedbackText("");
  // };

  const renderFindingsList = (findingsList: Finding[]) => {
    return findingsList.map((finding) => {
      const displayValue = useLocation ? finding.location : finding.frame_id;
      // const feedback = feedbackState[finding.finding_id];

      return (
        <div key={finding.finding_id} className="status-finding-item">
          <div className="finding-header">
            {showFrameId && finding.frame_id && (
              <span className="finding-title">Frame: {finding.frame_id}</span>
            )}
            {displayValue && (
              <span className="finding-title">Section: {displayValue}</span>
            )}
            {/* <div className="finding-feedback">
              <button
                className={`feedback-btn like-btn ${feedback?.liked ? "active" : ""}`}
                title="Like"
                onClick={() => handleLike(finding.finding_id)}
              >
                <LikeIcon fill={feedback?.liked ? "#BE2BBB" : "#9fa8b8"} />
              </button>
              <button
                className={`feedback-btn dislike-btn ${feedback?.disliked ? "active" : ""}`}
                title="Dislike"
                onClick={() => handleDislike(finding.finding_id)}
              >
                <DislikeIcon fill={feedback?.disliked ? "#BE2BBB" : "#9fa8b8"} />
              </button>
            </div> */}
          </div>
          <div className="finding-content">
            {finding.message && (
              <div className="finding-message">
                {renderFindingMessage(finding)}
              </div>
            )}
            {(finding.actual || finding.expected || finding.recommendation) && (
              <div className="finding-recommendation">
                {finding.recommendation || finding.expected || finding.actual}
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <div className="findings-by-status">
        {failedFindings.length > 0 && (
          <div className="status-section failed">
            <div
              className="status-header"
              onClick={() => toggleSection("failed")}
            >
              <Icon
                type={
                  expandedSections.has("failed") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <FailIcon className="status-icon" />
              <span className="status-title">
                Failed ({failedFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("failed") && (
              <div className="status-content">
                {renderFindingsList(failedFindings)}
              </div>
            )}
          </div>
        )}

        {warningFindings.length > 0 && (
          <div className="status-section warnings">
            <div
              className="status-header"
              onClick={() => toggleSection("warnings")}
            >
              <Icon
                type={
                  expandedSections.has("warnings")
                    ? "chevron-up"
                    : "chevron-down"
                }
                size="small"
              />
              <WarningIcon className="status-icon" />
              <span className="status-title">
                Observations ({warningFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("warnings") && (
              <div className="status-content">
                {renderFindingsList(warningFindings)}
              </div>
            )}
          </div>
        )}

        {passedFindings.length > 0 && (
          <div className="status-section passed">
            <div
              className="status-header"
              onClick={() => toggleSection("passed")}
            >
              <Icon
                type={
                  expandedSections.has("passed") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <Icon type="check-round" size="small" className="status-icon" />
              <span className="status-title">
                Passed ({passedFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("passed") && (
              <div className="status-content">
                {renderFindingsList(passedFindings)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      {/* {feedbackModal.isOpen && (
        <div className="feedback-modal-overlay" onClick={handleFeedbackCancel}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>Provide Feedback</h3>
              <button
                className="feedback-modal-close"
                onClick={handleFeedbackCancel}
              >
                <Icon type="close" size="small" />
              </button>
            </div>
            <div className="feedback-modal-body">
              <p>Please let us know why you disagree with this finding:</p>
              <textarea
                className="feedback-textarea"
                placeholder="Enter your feedback here..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={4}
              />
            </div>
            <div className="feedback-modal-footer">
              <button
                className="feedback-modal-btn cancel"
                onClick={handleFeedbackCancel}
              >
                Cancel
              </button>
              <button
                className="feedback-modal-btn submit"
                onClick={handleFeedbackSubmit}
                disabled={!feedbackText.trim()}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )} */}
    </>
  );
};

// Other findings component
const OtherFindings: React.FC<{
  findings: Finding[];
  expandedFindings: Set<string>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
  setExpandedFrames: React.Dispatch<React.SetStateAction<Set<string>>>;
}> = ({
  findings,
  expandedFindings,
  setExpandedFindings,
  setExpandedFrames,
}) => {
  // Group by check_id
  const findingsByCheckId = findings.reduce(
    (acc, finding) => {
      const checkId = finding.check_id || "unknown";
      if (!acc[checkId]) {
        acc[checkId] = [];
      }
      acc[checkId].push(finding);
      return acc;
    },
    {} as Record<string, Finding[]>,
  );

  return (
    <>
      {Object.entries(findingsByCheckId).map(([checkId, checkFindings]) => {
        const checkKey = `check_${checkId}`;

        // Only select if this check is selected
        const isSelected = expandedFindings.has(checkKey);

        const failedCount = checkFindings.filter(
          (f) => f.result === "FAIL",
        ).length;
        const warningCount = checkFindings.filter(
          (f) => f.result !== "PASS" && f.result !== "FAIL",
        ).length;

        // Get description from first finding's message or category
        const description = null;

        const handleCardSelect = () => {
          setExpandedFrames(new Set()); // Clear other section selections
          setExpandedFindings(new Set([checkKey]));
        };

        return (
          <div
            key={checkId}
            className={`frame-card check-card ${isSelected ? "selected" : ""}`}
            onClick={handleCardSelect}
          >
            <div className="check-card-body">
              <div className="check-card-title">{formatCheckId(checkId)}</div>
              {description && (
                <div className="check-card-description">{description}</div>
              )}
            </div>
            <div className="check-card-header">
              <div className="frame-card-counts">
                {warningCount > 0 && (
                  <span className="count-badge warning">{warningCount}</span>
                )}
                {failedCount > 0 && (
                  <span className="count-badge failed">{failedCount}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

// Findings by status component - groups findings by FAIL, warnings, PASS
const FindingsByStatus: React.FC<{
  findings: Finding[];
  expandedFindings: Set<string>;
  setExpandedFindings: React.Dispatch<React.SetStateAction<Set<string>>>;
  isResultsComplete?: boolean;
}> = ({
  findings,
  expandedFindings: _expandedFindings,
  setExpandedFindings: _setExpandedFindings,
  isResultsComplete = true,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["failed", "warnings"]),
  );
  // const [feedbackState, setFeedbackState] = useState<
  //   Record<string, { liked: boolean; disliked: boolean; feedback?: string }>
  // >({});
  // const [feedbackModal, setFeedbackModal] = useState<{
  //   isOpen: boolean;
  //   findingId: string | null;
  // }>({ isOpen: false, findingId: null });
  // const [feedbackText, setFeedbackText] = useState("");

  const failedFindings = findings.filter((f) => f.result === "FAIL");
  const warningFindings = findings.filter(
    (f) => f.result !== "PASS" && f.result !== "FAIL",
  );
  const passedFindings = findings.filter((f) => f.result === "PASS");

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  // const handleLike = (findingId: string) => {
  //   setFeedbackState((prev) => ({
  //     ...prev,
  //     [findingId]: { liked: true, disliked: false, feedback: undefined },
  //   }));
  // };

  // const handleDislike = (findingId: string) => {
  //   setFeedbackModal({ isOpen: true, findingId });
  //   setFeedbackText("");
  // };

  // const handleFeedbackSubmit = () => {
  //   const findingId = feedbackModal.findingId;
  //   if (findingId && feedbackText.trim()) {
  //     setFeedbackState((prev) => ({
  //       ...prev,
  //       [findingId]: { liked: false, disliked: true, feedback: feedbackText },
  //     }));
  //     setFeedbackModal({ isOpen: false, findingId: null });
  //     setFeedbackText("");
  //   }
  // };

  // const handleFeedbackCancel = () => {
  //   setFeedbackModal({ isOpen: false, findingId: null });
  //   setFeedbackText("");
  // };

  const renderFindingsList = (findingsList: Finding[], offset: number) => {
    return findingsList.map((finding, index) => {
      // const feedback = feedbackState[finding.finding_id];

      return (
        <div key={finding.finding_id} className="status-finding-item">
          <div className="finding-header">
            <span className="finding-title">
              Check {offset + index + 1}: {formatCheckId(finding.check_id)}
            </span>
            {/* <div className="finding-feedback">
              <button
                className={`feedback-btn like-btn ${feedback?.liked ? "active" : ""}`}
                title="Like"
                onClick={() => handleLike(finding.finding_id)}
              >
                <LikeIcon fill={feedback?.liked ? "#BE2BBB" : "#9fa8b8"} />
              </button>
              <button
                className={`feedback-btn dislike-btn ${feedback?.disliked ? "active" : ""}`}
                title="Dislike"
                onClick={() => handleDislike(finding.finding_id)}
              >
                <DislikeIcon fill={feedback?.disliked ? "#BE2BBB" : "#9fa8b8"} />
              </button>
            </div> */}
          </div>
          <div className="finding-content">
            {finding.message && (
              <div className="finding-message">
                {renderFindingMessage(finding)}
              </div>
            )}
            {(finding.actual || finding.expected || finding.recommendation) && (
              <div className="finding-recommendation">
                {finding.recommendation || finding.expected || finding.actual}
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  return (
    <>
      <div className="findings-by-status">
        {failedFindings.length > 0 && (
          <div className="status-section failed">
            <div
              className="status-header"
              onClick={() => toggleSection("failed")}
            >
              <Icon
                type={
                  expandedSections.has("failed") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <FailIcon className="status-icon" />
              <span className="status-title">
                Failed ({failedFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("failed") && (
              <div className="status-content">
                {renderFindingsList(failedFindings, 0)}
              </div>
            )}
          </div>
        )}

        {warningFindings.length > 0 && (
          <div className="status-section warnings">
            <div
              className="status-header"
              onClick={() => toggleSection("warnings")}
            >
              <Icon
                type={
                  expandedSections.has("warnings")
                    ? "chevron-up"
                    : "chevron-down"
                }
                size="small"
              />
              <WarningIcon className="status-icon" />
              <span className="status-title">
                Observations ({warningFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("warnings") && (
              <div className="status-content">
                {renderFindingsList(warningFindings, failedFindings.length)}
              </div>
            )}
          </div>
        )}

        {passedFindings.length > 0 && (
          <div className="status-section passed">
            <div
              className="status-header"
              onClick={() => toggleSection("passed")}
            >
              <Icon
                type={
                  expandedSections.has("passed") ? "chevron-up" : "chevron-down"
                }
                size="small"
              />
              <Icon type="check-round" size="small" className="status-icon" />
              <span className="status-title">
                Passed ({passedFindings.length})
                {!isResultsComplete && (
                  <Icon
                    type="loading"
                    size="small"
                    className="spinner-loader"
                  />
                )}
              </span>
            </div>
            {expandedSections.has("passed") && (
              <div className="status-content">
                {renderFindingsList(
                  passedFindings,
                  failedFindings.length + warningFindings.length,
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Feedback Modal */}
      {/* {feedbackModal.isOpen && (
        <div className="feedback-modal-overlay" onClick={handleFeedbackCancel}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>Provide Feedback</h3>
              <button
                className="feedback-modal-close"
                onClick={handleFeedbackCancel}
              >
                <Icon type="close" size="small" />
              </button>
            </div>
            <div className="feedback-modal-body">
              <p>Please let us know why you disagree with this finding:</p>
              <textarea
                className="feedback-textarea"
                placeholder="Enter your feedback here..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
                rows={4}
              />
            </div>
            <div className="feedback-modal-footer">
              <button
                className="feedback-modal-btn cancel"
                onClick={handleFeedbackCancel}
              >
                Cancel
              </button>
              <button
                className="feedback-modal-btn submit"
                onClick={handleFeedbackSubmit}
                disabled={!feedbackText.trim()}
              >
                Submit Feedback
              </button>
            </div>
          </div>
        </div>
      )}  */}
    </>
  );
};
